/**
 * Стартовый каталог активностей (BLUEPRINT 3.5). Сид переносит его в таблицу `Activity`.
 * `sizeOptions` пуст у неосновных: под них площадка подбирается по размеру отряда.
 */
export const ACTIVITY_CODES = [
  'padel',
  'volleyball',
  'football5',
  'tabletennis',
  'banya',
  'quiz',
  'workshop',
  'running',
  'tournament',
] as const;

export type ActivityCode = (typeof ACTIVITY_CODES)[number];

export interface ActivityDefinition {
  code: ActivityCode;
  name: string;
  description: string;
  /** Можно выбрать основной для отряда */
  isPrimary: boolean;
  /** Допустимые размеры отряда (только для основных) */
  sizeOptions: readonly number[];
  /** Минимум людей, чтобы сбор состоялся */
  minPlay: number;
  defaultDurationMin: number;
  indoor: boolean;
  needsLevel: boolean;
  /** Мест запасного в составе (ADR-10) */
  reserveSlots: number;
  /** Доля цены сезона для запасного */
  reservePriceRatio: number;
  rotationHint: string | null;
}

export const ACTIVITIES: readonly ActivityDefinition[] = [
  {
    code: 'padel',
    name: 'Падел',
    description: 'Два корта по четыре. Основной спорт и лучшая точка входа.',
    isPrimary: true,
    sizeOptions: [8],
    minPlay: 4,
    defaultDurationMin: 90,
    indoor: true,
    needsLevel: true,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'volleyball',
    name: 'Волейбол',
    description: 'Шесть на шесть в зале.',
    isPrimary: true,
    sizeOptions: [12],
    minPlay: 8,
    defaultDurationMin: 120,
    indoor: true,
    needsLevel: true,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'football5',
    name: 'Мини-футбол',
    description: 'Пять на пять, крытое поле.',
    isPrimary: true,
    sizeOptions: [10],
    minPlay: 8,
    defaultDurationMin: 90,
    indoor: true,
    needsLevel: true,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'tabletennis',
    name: 'Настольный теннис',
    description: 'Вдвоём за одним столом или вчетвером: пары и круг. Самый быстрый набор.',
    isPrimary: true,
    sizeOptions: [2, 4],
    minPlay: 2,
    defaultDurationMin: 120,
    indoor: true,
    needsLevel: true,
    reserveSlots: 1,
    reservePriceRatio: 0.75,
    // Раунд 4, N5: «двое играют, один отдыхает» неверно для стола на четверых
    rotationHint:
      'За столом двое или две пары, запасной выходит на смену каждые 15–20 минут или по партии.',
  },
  {
    code: 'banya',
    name: 'Баня',
    description: 'Парение на состав. Кабинет на 4–8; большой отряд — два кабинета.',
    isPrimary: false,
    sizeOptions: [],
    minPlay: 3,
    defaultDurationMin: 180,
    indoor: true,
    needsLevel: false,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'quiz',
    name: 'Квиз',
    description: 'Квиз, мафия или настолки. До 9 на команду.',
    isPrimary: false,
    sizeOptions: [],
    minPlay: 4,
    defaultDurationMin: 150,
    indoor: true,
    needsLevel: false,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'workshop',
    name: 'Мастерская',
    description: 'Мастерская, стрельба или картинг. Разовая программа на 4–12.',
    isPrimary: false,
    sizeOptions: [],
    minPlay: 4,
    defaultDurationMin: 120,
    indoor: false,
    needsLevel: false,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'running',
    name: 'Бег',
    description: 'Бесплатный бонус-сбор. Любое число людей.',
    isPrimary: false,
    sizeOptions: [],
    minPlay: 2,
    defaultDurationMin: 60,
    indoor: false,
    needsLevel: false,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
  {
    code: 'tournament',
    name: 'Турнир',
    description: 'Шестой сбор: против другого отряда того же уровня по основной активности.',
    isPrimary: false,
    sizeOptions: [],
    minPlay: 4,
    defaultDurationMin: 120,
    indoor: true,
    needsLevel: false,
    reserveSlots: 0,
    reservePriceRatio: 1,
    rotationHint: null,
  },
];

export const ACTIVITY_BY_CODE: Readonly<Record<ActivityCode, ActivityDefinition>> =
  Object.fromEntries(ACTIVITIES.map((a) => [a.code, a])) as Record<
    ActivityCode,
    ActivityDefinition
  >;

export const PRIMARY_ACTIVITY_CODES = ACTIVITIES.filter((a) => a.isPrimary).map((a) => a.code);
